import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import httpInstances from '@/api/axiosInstance';
import { DEFAULT_DASHBOARD_ID } from '@/features/Dashboard/constants/const';
import {
  normalizeDashboardTabs,
  normalizeTabEntry,
  processMarketApps
} from '@/features/Dashboard/lib/transformers';
import { DashboardResponse, DashboardTab } from '@/features/Dashboard/types';
import { useTripTokenFromStorage } from '@/services/storage';
import { generateUniqueUUID } from '@/utils/generateUniqueUUID'; // Query Keys helper – colocated with feature API

// Query Keys helper – colocated with feature API
export const dashboardQueryKeys = {
  all: ['dashboard'] as const,
  list: (rename: boolean, token: string | null) => ['dashboard', rename, token] as const
};

export const useDashboard = (rename = true) => {
  const token = useTripTokenFromStorage();
  return useQuery({
    queryKey: dashboardQueryKeys.list(rename, token),
    queryFn: async () => {
      const { data } =
        await httpInstances.axiosInternalInstance.get<Record<string, DashboardResponse>>(
          `/config/dashboard`
        );

      const result: Record<string, DashboardTab> = Object.fromEntries(
        Object.entries(data).map(([name, dashboardData]) => {
          const dashboardName = name === DEFAULT_DASHBOARD_ID && rename ? 'My Dashboard' : name;
          const value = dashboardData as any;
          const dashboardTabs: DashboardTab =
            normalizeDashboardTabs(value, name, dashboardName) ??
            ({
              Main: { id: generateUniqueUUID(new Set()), content: [] }
            } as DashboardTab);

          // Process MarketApps in each tab's content
          const processedTabs: DashboardTab = Object.fromEntries(
            Object.entries(dashboardTabs).map(([tabName, tabValue]) => {
              const tabEntry = normalizeTabEntry(tabName, tabValue);
              return [
                tabName,
                {
                  ...tabEntry,
                  content: processMarketApps(tabEntry.content)
                }
              ];
            })
          );

          return [dashboardName, processedTabs];
        })
      );

      return result;
    },
    retry: (failureCount, error) => {
      if (error instanceof AxiosError && error.response?.status === 404) {
        return false;
      }
      return failureCount < 3;
    },
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    structuralSharing: false
  });
};

export type { MarketApps } from '@/features/Dashboard/types';
